"use client";

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from '@/hooks/use-toast';
import { Loader2, Sparkles, Link as LinkIcon, Check, Wand2 } from 'lucide-react';
import type { Tree } from '@/lib/types';
import { updateTreeFromUrl, UpdateTreeFromUrlOutput } from '@/ai/flows/update-tree-from-url-flow';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface UpdateTreeDialogProps {
  tree: Tree;
}

export function UpdateTreeDialog({ tree }: UpdateTreeDialogProps) {
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [updatedData, setUpdatedData] = useState<UpdateTreeFromUrlOutput | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleScrape = async () => {
    if (!url || !url.startsWith('http')) {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL.',
        variant: 'destructive',
      });
      return;
    }

    setIsScraping(true);
    setUpdatedData(null);
    try {
      const result = await updateTreeFromUrl({
        url,
        tree: {
            commonName: tree.commonName,
            scientificName: tree.scientificName,
            description: tree.description,
            uses: tree.uses,
            funFacts: tree.funFacts,
        },
      });
      setUpdatedData(result);
      toast({
        title: 'Scraping Successful!',
        description: 'Review the updated information below.',
      });
    } catch (error) {
      console.error('Scraping error:', error);
      toast({
        title: 'Scraping Failed',
        description: 'Could not extract information from the provided URL. Please try another source.',
        variant: 'destructive',
      });
    } finally {
      setIsScraping(false);
    }
  };
  
  const handleAccept = () => {
    // Here you would typically update your backend.
    // Since we're using a static data file, we'll just show a success message.
     toast({
        title: 'Update Accepted!',
        description: `${updatedData?.commonName} data has been updated. (Note: This is a simulation and does not persist).`,
     });
     setIsOpen(false);
     setUpdatedData(null);
     setUrl('');
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
        setUpdatedData(null);
        setUrl('');
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Wand2 className="mr-2" /> Update with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-headline text-2xl">Update Tree from URL</DialogTitle>
          <DialogDescription>
            Provide a URL to a webpage with information about the {tree.commonName}. Our AI will scrape it and update the details.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input 
                        placeholder="https://example.com/about-oaks" 
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Button onClick={handleScrape} disabled={isScraping || !url}>
                    {isScraping ? <Loader2 className="animate-spin" /> : <Sparkles />}
                    <span className="ml-2">Scrape</span>
                </Button>
            </div>
            
            {updatedData && (
                <Card className="bg-secondary/50 max-h-96 overflow-y-auto">
                    <CardHeader>
                        <CardTitle>Review Suggested Updates</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                        <div>
                            <h4 className="font-semibold">Common Name</h4>
                            <p className="text-muted-foreground">{updatedData.commonName}</p>
                        </div>
                         <div>
                            <h4 className="font-semibold">Scientific Name</h4>
                            <p className="text-muted-foreground">{updatedData.scientificName}</p>
                        </div>
                        <div>
                            <h4 className="font-semibold">Description</h4>
                            <p className="text-muted-foreground">{updatedData.description}</p>
                        </div>
                        <div>
                            <h4 className="font-semibold">Fun Fact</h4>
                            <p className="text-muted-foreground">{updatedData.funFacts}</p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={handleAccept} disabled={!updatedData}>
            <Check className="mr-2" /> Accept Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
